import { useLayoutEffect, useRef, useState } from "react";

export const LEAVE_MS = 360;

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

type Gone<T> = { row: T; index: number };
export type ShownRow<T> = { row: T; ghost: boolean; index: number };

export function useLeavingRows<T extends { id?: unknown }>(
  rows: T[],
  viewKey: string,
): ShownRow<T>[] {
  const previous = useRef({ rows, viewKey });
  const [ghosts, setGhosts] = useState<Gone<T>[]>([]);

  useLayoutEffect(() => {
    const before = previous.current;
    previous.current = { rows, viewKey };
    if (before.viewKey !== viewKey || prefersReducedMotion()) {
      setGhosts((current) => (current.length ? [] : current));
      return;
    }
    const kept = new Set(rows.map((row) => String(row.id)));
    const gone = before.rows
      .map((row, index) => ({ row, index }))
      .filter((item) => !kept.has(String(item.row.id)));
    if (!gone.length) return;
    setGhosts((current) => [...current, ...gone]);
    setTimeout(
      () => setGhosts((current) => current.filter((g) => !gone.includes(g))),
      LEAVE_MS,
    );
  }, [rows, viewKey]);

  const shown = rows.map((row, index) => ({ row, ghost: false, index }));
  [...ghosts]
    .sort((a, b) => a.index - b.index)
    .forEach((g) =>
      shown.splice(Math.min(g.index, shown.length), 0, {
        row: g.row,
        ghost: true,
        index: -1,
      }),
    );
  return shown;
}
